/* import __COLOCATED_TEMPLATE__ from './fin-resolution-state-filter.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { REPORTING_FILTER_SELECT_ALL } from 'embercom/lib/reporting/flexible/constants';
import type IntlService from 'embercom/services/intl';
import type { SafeString } from '@ember/template/-private/handlebars';
import { isEmpty } from '@ember/utils';
import { type SelectedFilter } from 'embercom/services/filter-pattern-service';
import { type FilterBarItem } from 'embercom/services/filter-pattern-service';

interface Args {
  initiallyOpened: boolean;
  icon: string;
  type: string;
  name: string;
  label: string;
  onClose: () => void;
  selected: SelectedFilter;
  setSelected: (filterValues: string[], operator?: string) => void;
  analyticsObject: string;
  removeFilter: () => void;
  isDisabled: boolean;
}

export enum ResolutionState {
  ConfirmedResolved = 'confirmed_resolved',
  AssumedResolved = 'assumed_resolved',
  RoutedToTeam = 'routed_to_team',
  NegativeFeedback = 'negative_feedback',
  ProcedureHandoff = 'procedure_handoff',
  Any = REPORTING_FILTER_SELECT_ALL,
}

interface ResolutionStateType {
  [key: string]: SafeString | string;
}

const ANALYTIC_NAME = 'fin_resolution_state';

export default class FinResolutionStateFilter extends Component<Args> {
  @service declare appService: AppService;
  @service intercomEventService: any;
  @service intl!: IntlService;
  @tracked label = this.currentLabel;
  @tracked selectedDropdownItems: string[] = this.args.selected.values || [];
  @tracked operator: string = this.args.selected.operator || 'category';

  get currentLabel() {
    if (isEmpty(this.selectedResolutionStates)) {
      return this.anyLabel;
    } else {
      let translatedResolutionStates = this.selectedResolutionStates.map(
        (value) => this.resolutionLabels[value],
      );
      return translatedResolutionStates.join(', ');
    }
  }

  get selectedResolutionStates() {
    return this.selectedDropdownItems.filter((item) => item !== ResolutionState.Any);
  }

  private get resolutionLabels(): ResolutionStateType {
    return {
      [ResolutionState.ConfirmedResolved]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.fin-resolution-state.confirmed-resolved',
      ),
      [ResolutionState.AssumedResolved]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.fin-resolution-state.assumed-resolved',
      ),
      [ResolutionState.RoutedToTeam]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.fin-resolution-state.routed-to-team',
      ),
      [ResolutionState.NegativeFeedback]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.fin-resolution-state.negative-feedback',
      ),
      [ResolutionState.ProcedureHandoff]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.fin-resolution-state.procedure-handoff',
      ),
    };
  }

  private get anyLabel(): string {
    return this.intl.t(
      'components.reporting.custom.chart-builder.filter-bar.fin-resolution-state.any',
    );
  }

  private mapToDropdownItems(resolutionStates: ResolutionStateType): FilterBarItem[] {
    return Object.entries(resolutionStates).map(([key, label]) => ({
      text: label.toString(),
      value: key,
      isSelected: this.selectedDropdownItems.includes(key),
    }));
  }

  get allItems() {
    return this.mapToDropdownItems(this.resolutionLabels);
  }

  @action
  trackAnalytics() {
    this.intercomEventService.trackAnalyticsEvent({
      action: ANALYTIC_NAME,
      object: this.args.analyticsObject,
      operator: this.operator,
    });
  }

  @action
  onClose() {
    if (this.isDestroying) {
      return;
    }

    this.label = this.currentLabel;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::ChartBuilder::FilterBar::FinResolutionStateFilter': typeof FinResolutionStateFilter;
    'reporting/custom/chart-builder/filter-bar/fin-resolution-state-filter': typeof FinResolutionStateFilter;
  }
}
